import { useCallback } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { useCachedQuery } from "./useCachedQuery";

export type StatusWindowHours = 4 | 24 | 168;

// Parse a JSON API response defensively. A 200 whose body is HTML — the SPA
// index.html CloudFront serves when an /api route 404s mid-deploy — would
// otherwise surface as a raw "Unexpected token '<'" out of res.json(). Turn
// that into a calm, actionable message the panels can render instead.
const readJson = async <T>(res: Response, label: string): Promise<T> => {
  if (!res.ok) throw new Error(`Failed to load ${label} (HTTP ${res.status})`);
  const body = await res.text();
  try {
    return JSON.parse(body) as T;
  } catch {
    if (body.trimStart().startsWith("<")) {
      throw new Error(`${label} is temporarily unavailable — the API may be deploying. Try again shortly.`);
    }
    throw new Error(`Couldn't load ${label} — unexpected response.`);
  }
};

// Mirrors HealthBucketView / TenantHealthTimelineView / FleetHealthHistoryView.
export type HealthBucket = {
  t: string;
  state: "HEALTHY" | "DEGRADED" | "DOWN" | "UNREACHABLE" | null;
  activity: boolean;
  reasons: ReadonlyArray<string>;
};

export type TenantHealthTimeline = {
  tenantId: string;
  current: string | null;
  uptimePct: number | null;
  installedVersion: string | null;
  runningVersion: string | null;
  buckets: ReadonlyArray<HealthBucket>;
};

export type FleetHealthHistory = {
  windowHours: number;
  bucketSeconds: number;
  from: string;
  tenants: ReadonlyArray<TenantHealthTimeline>;
};

// Mirrors UsagePointView / TenantUsageHistoryView.
export type UsagePoint = {
  t: string;
  deltaUsd: number;
  spentUsd: number | null;
};

export type TenantUsageHistory = {
  provider: string | null;
  periodLabel: string | null;
  limitUsd: number | null;
  latestSpentUsd: number | null;
  latestSampledAt: string | null;
  bucketSeconds: number;
  points: ReadonlyArray<UsagePoint>;
};

export const fleetHistoryUrl = (hours: StatusWindowHours): string =>
  `/api/admin/claws/health/history?hours=${hours}`;

export type UseAdminStatusResult = {
  status: "loading" | "ready" | "error";
  history: FleetHealthHistory | null;
  error: string | null;
  isFetching: boolean;
  refetch: () => Promise<void>;
};

export const useAdminStatus = (hours: StatusWindowHours): UseAdminStatusResult => {
  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<FleetHealthHistory> => {
      const res = await fetch(fleetHistoryUrl(hours), { credentials: "include", signal });
      return readJson<FleetHealthHistory>(res, "Status history");
    },
    [hours],
  );
  const { status, data, error, isFetching, refetch } = useCachedQuery<FleetHealthHistory>({
    // Cache only the default window; other windows are network-only so a
    // 7d payload never masquerades as the 24h strip on a cold paint.
    cacheResource: hours === 24 ? ADMIN_QUERIES.status.resource : null,
    queryKey: fleetHistoryUrl(hours),
    fetcher,
  });
  return { status, history: data, error, isFetching, refetch };
};

export const fetchUsageHistory = async (
  tenantId: string,
  hours: number,
  signal?: AbortSignal,
): Promise<TenantUsageHistory> => {
  const res = await fetch(
    `/api/admin/claws/${encodeURIComponent(tenantId)}/usage-history?hours=${hours}`,
    { credentials: "include", signal: signal ?? null },
  );
  return readJson<TenantUsageHistory>(res, "AI spend");
};

// Memory-search embedding spend. Its own series rather than a slice of the
// spend above: it bills to a different, guardrail-restricted key, so the two
// have independent totals, limits and reset periods.
export const fetchEmbeddingUsageHistory = async (
  tenantId: string,
  hours: number,
  signal?: AbortSignal,
): Promise<TenantUsageHistory> => {
  const res = await fetch(
    `/api/admin/claws/${encodeURIComponent(tenantId)}/embedding-usage-history?hours=${hours}`,
    { credentials: "include", signal: signal ?? null },
  );
  return readJson<TenantUsageHistory>(res, "Embedding spend");
};

// Per-tenant health timeline for the instance dashboard. Same shape as a
// single entry of the fleet history, but its own endpoint so a 30d window
// costs one tenant's rows, not the whole fleet's.
export const fetchTenantHealthHistory = async (
  tenantId: string,
  hours: number,
  signal?: AbortSignal,
): Promise<TenantHealthTimeline> => {
  const res = await fetch(
    `/api/admin/claws/${encodeURIComponent(tenantId)}/health-history?hours=${hours}`,
    { credentials: "include", signal: signal ?? null },
  );
  return readJson<TenantHealthTimeline>(res, "Health history");
};

// Mirrors ToolCallPointView / TenantToolCallHistoryView.
export type ToolCallPoint = {
  t: string;
  ok: number;
  error: number;
};

export type TenantToolCallHistory = {
  bucketSeconds: number;
  totalOk: number;
  totalError: number;
  points: ReadonlyArray<ToolCallPoint>;
};

export const fetchToolCallHistory = async (
  tenantId: string,
  hours: number,
  signal?: AbortSignal,
): Promise<TenantToolCallHistory> => {
  const res = await fetch(
    `/api/admin/claws/${encodeURIComponent(tenantId)}/tool-calls/timeline?hours=${hours}`,
    { credentials: "include", signal: signal ?? null },
  );
  return readJson<TenantToolCallHistory>(res, "Tool calls");
};

// Mirrors MetricPointView / MetricSeriesView / TenantMetricsView. value === null
// means no datapoint in that bucket (the chart breaks the line there).
export type MetricPoint = {
  t: string;
  value: number | null;
};

export type MetricSeries = {
  metric: string;
  points: ReadonlyArray<MetricPoint>;
};

export type TenantMetrics = {
  bucketSeconds: number;
  resolutionSeconds: number;
  coarse: boolean;
  series: ReadonlyArray<MetricSeries>;
  // Instance capacity (GiB) for turning the mem/disk percentages into absolute
  // figures in the tooltip; null when unknown (e.g. local dev, unmapped type).
  memTotalGib: number | null;
  diskTotalGib: number | null;
};

export const fetchTenantMetrics = async (
  tenantId: string,
  hours: number,
  signal?: AbortSignal,
): Promise<TenantMetrics> => {
  const res = await fetch(
    `/api/admin/claws/${encodeURIComponent(tenantId)}/metrics?hours=${hours}`,
    { credentials: "include", signal: signal ?? null },
  );
  return readJson<TenantMetrics>(res, "System metrics");
};

// Mirrors SnapshotView / TenantSnapshotsView.
export type Snapshot = {
  snapshotId: string;
  startedAt: string;
  sizeGiB: number;
  state: string;
  type: string;
  sourceVolumeId: string | null;
};

export type TenantSnapshots = {
  snapshots: ReadonlyArray<Snapshot>;
};

export const fetchTenantSnapshots = async (
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantSnapshots> => {
  const res = await fetch(
    `/api/admin/claws/${encodeURIComponent(tenantId)}/snapshots`,
    { credentials: "include", signal: signal ?? null },
  );
  return readJson<TenantSnapshots>(res, "Restore points");
};

/* Per-feature token usage for the plugins a tenant runs.
 *
 * ATTRIBUTION IS APPROXIMATE. A plugin never calls an LLM: its work happens
 * inside OpenClaw agent turns that a cron trigger fired, and usage is reported
 * per turn. One turn can drain several queue items, so a feature's share is
 * what its trigger INTENDED, not what was measured. `attribution` carries that
 * from the backend so the UI cannot quietly drop it.
 */
export type PluginFeatureUsage = {
  readonly feature: string;
  readonly passes: number;
  readonly errors: number;
  readonly inputTokens: number;
  readonly outputTokens: number;
};

export type PluginLastPass = {
  readonly at: string;
  /** Null on the CloudWatch path, which cannot say which feature ran last. */
  readonly feature: string | null;
  readonly status: string;
};

export type PluginPassUsage = {
  readonly passId: string;
  readonly at: string;
  readonly feature: string;
  readonly attributed: boolean;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly status: string;
  readonly model: string | null;
  readonly durationMs: number;
};

export type TenantPluginUsage = {
  readonly pluginId: string;
  readonly name: string;
  readonly version: string | null;
  readonly windowHours: number;
  readonly attribution: string;
  readonly passes: number;
  readonly errors: number;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly busiestFeature: string | null;
  readonly lastPass: PluginLastPass | null;
  readonly features: ReadonlyArray<PluginFeatureUsage>;
  /** Newest first. Empty where the transport cannot carry individual passes. */
  readonly recentPasses: ReadonlyArray<PluginPassUsage>;
};

export const fetchTenantPluginUsage = async (
  tenantId: string,
  hours: number,
  signal?: AbortSignal,
): Promise<ReadonlyArray<TenantPluginUsage>> => {
  const res = await fetch(
    `/api/admin/claws/${encodeURIComponent(tenantId)}/plugin-usage?hours=${hours}`,
    { credentials: "include", signal: signal ?? null },
  );
  return readJson<ReadonlyArray<TenantPluginUsage>>(res, "Plugin usage");
};

// Mirrors ModelUsageRow / ModelUsageView — usage of the models running ON the
// tenant's box (whisper.cpp transcription, llama.cpp embeddings). `kind` is
// "transcription" | "embedding" but stays a string: a new on-box model kind
// must not blank the panel just because the frontend hasn't shipped yet.
export type ModelUsageRow = {
  kind: string;
  model: string;
  invocations: number | null;
  audioSeconds: number | null;
  inputTokens: number | null;
  avgDurationMs: number | null;
};

export type ModelUsage = {
  windowHours: number;
  rows: ReadonlyArray<ModelUsageRow>;
  // True while embedding tokens are derived from cache-row deltas rather than
  // measured. The panel must not present them as fact when this is set.
  tokensEstimated: boolean;
  readAt: string;
};

export const fetchTenantModelUsage = async (
  tenantId: string,
  hours: number,
  signal?: AbortSignal,
): Promise<ModelUsage> => {
  const res = await fetch(
    `/api/admin/claws/${encodeURIComponent(tenantId)}/model-usage?hours=${hours}`,
    { credentials: "include", signal: signal ?? null },
  );
  return readJson<ModelUsage>(res, "On-box model usage");
};
